import React from 'react';
import {
  List, Datagrid, TextField, DateField, EditButton, ShowButton,
  Edit, SimpleForm, DateInput, NumberInput, TextInput,
  Create, ReferenceInput, SelectInput, AutocompleteInput, ReferenceField, NumberField, useRecordContext,
  FilterList, FilterLiveSearch, Filter, SearchInput, DateInput as FilterDateInput,
  useListContext, Show, TabbedShowLayout, Tab, useGetList, useGetOne,
  FormDataConsumer,
} from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { Card, CardContent, Typography, Box, TextField as MuiTextField, Chip } from '@mui/material';

const CommissionCalculator = ({ formData }) => {
  const { data: sourceData } = useGetOne(
    'booking-sources',
    { id: formData.booking_source },
    { enabled: Boolean(formData.booking_source) }
  );
  const form = useFormContext();

  React.useEffect(() => {
    if (!sourceData || !formData.price) return;
    const rate = parseFloat(sourceData.total_rate) || 0;
    const price = parseFloat(formData.price) || 0;
    const commission = Math.round(price * rate / 100 * 100) / 100;
    form.setValue('commission', commission, { shouldDirty: true });
    form.setValue('total', Math.round((price - commission) * 100) / 100, { shouldDirty: true });
  }, [formData.booking_source, formData.price, sourceData]); // eslint-disable-line

  if (!sourceData || !formData.booking_source) return null;
  return (
    <Box sx={{ mb: 1, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, width: '100%' }}>
      <Typography variant="caption" color="text.secondary">
        Stawka prowizji: {sourceData.commission_rate}% prowizja + {sourceData.payment_fee_rate}% opłata płatnicza = {sourceData.total_rate}% łącznie
      </Typography>
    </Box>
  );
};

const today = new Date().toISOString().split('T')[0];
const todayDate = new Date(today);

// At property / days to arrival (for list views)
export const AtPropertyOrArrivalField = () => {
  const record = useRecordContext();
  if (!record) return null;
  const checkIn = record.check_in;
  const checkOut = record.check_out;
  if (checkIn && checkOut && checkIn <= today && today <= checkOut) {
    const checkOutDate = new Date(checkOut);
    const diffTime = checkOutDate - todayDate;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return (
      <span style={{ color: 'green', fontWeight: 'bold' }}>
        Current ({diffDays} days left)
      </span>
    );
  }
  if (checkIn) {
    const checkInDate = new Date(checkIn);
    const diffTime = checkInDate - todayDate;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays > 0) {
      return (
        <span style={{ color: '#1976d2', fontWeight: 'bold' }}>
          {diffDays} days to arrival
        </span>
      );
    }
    if (diffDays === 0) {
      return (
        <span style={{ color: '#1976d2', fontWeight: 'bold' }}>
          Arriving today
        </span>
      );
    }
  }
  return null;
};

// Custom field to calculate number of nights
export const NightsField = () => {
  const record = useRecordContext();
  if (!record) return null;
  const { check_in, check_out } = record;
  if (!check_in || !check_out) return null;
  const checkInDate = new Date(check_in);
  const checkOutDate = new Date(check_out);
  const diffTime = checkOutDate - checkInDate;
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
  return <span>{diffDays}</span>;
};

// Custom date filter component for sidebar
const DateFilter = ({ source, label, defaultValue }) => {
  const { setFilters, filterValues } = useListContext();
  
  const handleDateChange = (event) => {
    const newFilters = { ...filterValues };
    if (event.target.value) {
      newFilters[source] = event.target.value;
    } else {
      delete newFilters[source];
    }
    setFilters(newFilters);
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>
      <MuiTextField
        type="date"
        size="small"
        fullWidth
        defaultValue={defaultValue}
        onChange={handleDateChange}
        InputLabelProps={{
          shrink: true,
        }}
      />
    </Box>
  );
};

// Custom sidebar filter component for bookings
const BookingFilterSidebar = () => {
  // Get first day of current month as default
  const getFirstDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  };

  return (
    <Card sx={{ order: -1, mr: 2, mt: 1, minWidth: '250px' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        
        <FilterList
          label="Client Name"
          source="guest__name"
        >
          <FilterLiveSearch source="guest__name" />
        </FilterList>

        <DateFilter
          source="check_in_gte"
          label="Check-in from"
          defaultValue={getFirstDayOfMonth()}
        />
      </CardContent>
    </Card>
  );
};

// Additional filter component for status only
const BookingFilter = (props) => {
  return (
    <Filter {...props}>
      <SelectInput
        source="status"
        label="Status"
        choices={[
          { id: 'confirmed', name: 'Confirmed' },
          { id: 'pending', name: 'Pending' },
          { id: 'cancelled', name: 'Cancelled' },
        ]}
      />
    </Filter>
  );
};

export const BookingList = props => (
  <List 
    {...props} 
    aside={<BookingFilterSidebar />}
    filters={<BookingFilter />}
    filterDefaultValues={{ check_in_gte: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0] }}
  >
    <Datagrid rowClick="show">
      <TextField source="id" />
      <DateField source="created_at" showTime={true} />
      <ReferenceField source="booking_source" reference="booking-sources" label="Źródło" emptyText="—">
        <TextField source="name" />
      </ReferenceField>
      <DateField source="check_in" />
      <DateField source="check_out" />
      <NumberField source="num_guests" />
      <NumberField source="total" options={{ style: 'currency', currency: 'PLN' }} />
      <NumberField source="commission" options={{ style: 'currency', currency: 'PLN' }} label="Prowizja" />
      <NightsField label="Nights" />
      <ReferenceField source="guest" reference="guests">
        <TextField source="name" />
      </ReferenceField>
      <ReferenceField source="apartment" reference="apartments">
        <TextField source="name" />
      </ReferenceField>
      <ShowButton />
    </Datagrid>
  </List>
);

export const BookingEdit = props => (
  <Edit {...props}>
    <SimpleForm>
      <TextInput disabled source="id" />
      <ReferenceInput
        source="guest"
        reference="guests"
      >
        <AutocompleteInput optionText="name" />
      </ReferenceInput>
      <ReferenceInput source="apartment" reference="apartments"><SelectInput optionText="name" /></ReferenceInput>
      <DateInput source="check_in" />
      <DateInput source="check_out" />
      <NumberInput source="num_guests" />
      <ReferenceInput source="booking_source" reference="booking-sources">
        <SelectInput optionText="name" label="Źródło rezerwacji" />
      </ReferenceInput>
      <NumberInput source="price" label="Cena brutto" />
      <FormDataConsumer>
        {({ formData }) => <CommissionCalculator formData={formData} />}
      </FormDataConsumer>
      <NumberInput source="commission" label="Prowizja (auto)" helperText="Automatycznie wyliczana na podstawie źródła" />
      <NumberInput source="discount" label="Rabat" />
      <NumberInput source="total" label="Kwota netto (auto)" helperText="Automatycznie wyliczana: cena - prowizja" />
      <TextInput source="status" />
      <TextInput source="payment_status" />
    </SimpleForm>
  </Edit>
);

// Component to display customer's other bookings
const CustomerBookingsTab = () => {
  const record = useRecordContext();
  
  if (!record?.guest?.id) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography color="text.secondary">
          No customer information available.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Other Bookings by {record?.guest?.name}
      </Typography>
      <List
        resource="bookings"
        filter={{ guest_id: record.guest.id }}
        sort={{ field: 'check_in', order: 'DESC' }}
        perPage={50}
        disableSyncWithLocation
        disablePagination
      >
        <Datagrid rowClick="show">
          <TextField source="id" />
          <DateField source="created_at" showTime={true} />
          <ReferenceField source="booking_source" reference="booking-sources" label="Źródło" emptyText="—">
            <TextField source="name" />
          </ReferenceField>
          <DateField source="check_in" />
          <DateField source="check_out" />
          <NumberField source="num_guests" />
          <NumberField source="total" options={{ style: 'currency', currency: 'PLN' }} />
          <NightsField label="Nights" />
          <ReferenceField source="guest" reference="guests">
            <TextField source="name" />
          </ReferenceField>
          <ReferenceField source="apartment" reference="apartments">
            <TextField source="name" />
          </ReferenceField>
          <ShowButton />
          <EditButton />
        </Datagrid>
      </List>
    </Box>
  );
};

// Status chip component
const StatusChip = ({ source }) => {
  const record = useRecordContext();
  const status = record[source];
  const getColor = (status) => {
    switch (status) {
      case 'confirmed': return 'success';
      case 'pending': return 'warning';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };
  
  return <Chip label={status} color={getColor(status)} size="small" />;
};

// Custom field component for better display - fixed width labels with left-aligned values
const InfoField = ({ label, children, ...props }) => (
  <Box sx={{ 
    display: 'flex', 
    alignItems: 'center',
    mb: 1.5,
    py: 0.5,
    borderBottom: '1px solid',
    borderColor: 'divider'
  }}>
    <Typography variant="subtitle2" color="text.secondary" sx={{ 
      fontWeight: 'bold', 
      width: '300px',
      flexShrink: 0
    }}>
      {label}
    </Typography>
    <Box sx={{ ml: 2 }}>
      {children}
    </Box>
  </Box>
);

export const BookingShow = props => (
  <Show {...props}>
    <TabbedShowLayout>
      <Tab label="Booking Details">
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
          {/* Left Column */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Booking Information */}
            <Card sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 1.5 }}>
                Booking Information
              </Typography>
              <InfoField label="Booking ID">
                <TextField source="id" />
              </InfoField>
              <InfoField label="Check-in Date">
                <DateField source="check_in" />
              </InfoField>
              <InfoField label="Check-out Date">
                <DateField source="check_out" />
              </InfoField>
              <InfoField label="Number of Nights">
                <NightsField />
              </InfoField>
              <InfoField label="Number of Guests">
                <NumberField source="num_guests" />
              </InfoField>
              <InfoField label="Źródło rezerwacji">
                <ReferenceField source="booking_source" reference="booking-sources" emptyText="—">
                  <TextField source="name" />
                </ReferenceField>
              </InfoField>
              <InfoField label="Apartment">
                <ReferenceField source="apartment" reference="apartments">
                  <TextField source="name" />
                </ReferenceField>
              </InfoField>
              <InfoField label="Created At">
                <DateField source="created_at" showTime={true} />
              </InfoField>
            </Card>

            {/* Pricing Information */}
            <Card sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 1.5 }}>
                Pricing Information
              </Typography>
              <InfoField label="Cena brutto">
                <NumberField source="price" options={{ style: 'currency', currency: 'PLN' }} />
              </InfoField>
              <InfoField label="Prowizja">
                <NumberField source="commission" options={{ style: 'currency', currency: 'PLN' }} />
              </InfoField>
              <InfoField label="Rabat">
                <NumberField source="discount" options={{ style: 'currency', currency: 'PLN' }} />
              </InfoField>
              <InfoField label="Kwota netto">
                <NumberField source="total" options={{ style: 'currency', currency: 'PLN' }} />
              </InfoField>
            </Card>
          </Box>

          {/* Right Column */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Customer Information */}
            <Card sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 1.5 }}>
                Customer Information
              </Typography>
              <InfoField label="Name">
                <ReferenceField source="guest" reference="guests">
                  <TextField source="name" />
                </ReferenceField>
              </InfoField>
              <InfoField label="Email">
                <ReferenceField source="guest" reference="guests">
                  <TextField source="email" />
                </ReferenceField>
              </InfoField>
              <InfoField label="Phone">
                <ReferenceField source="guest" reference="guests">
                  <TextField source="phone" />
                </ReferenceField>
              </InfoField>
              <InfoField label="Nationality">
                <ReferenceField source="guest" reference="guests">
                  <TextField source="nationality" />
                </ReferenceField>
              </InfoField>
              <InfoField label="ID Document">
                <ReferenceField source="guest" reference="guests">
                  <TextField source="id_document" />
                </ReferenceField>
              </InfoField>
            </Card>

            {/* Contact Details */}
            <Card sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 1.5 }}>
                Contact Details
              </Typography>
              <InfoField label="Address">
                <ReferenceField source="guest" reference="guests">
                  <TextField source="address" />
                </ReferenceField>
              </InfoField>
              <InfoField label="Notes">
                <ReferenceField source="guest" reference="guests">
                  <TextField source="notes" />
                </ReferenceField>
              </InfoField>
            </Card>

            {/* Status Information */}
            <Card sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 1.5 }}>
                Status Information
              </Typography>
              <InfoField label="Booking Status">
                <StatusChip source="status" />
              </InfoField>
              <InfoField label="Payment Status">
                <StatusChip source="payment_status" />
              </InfoField>
            </Card>
          </Box>
        </Box>
      </Tab>
      
      <Tab label="Customer's Other Bookings">
        <CustomerBookingsTab />
      </Tab>
    </TabbedShowLayout>
  </Show>
);

export const BookingCreate = props => (
  <Create {...props}>
    <SimpleForm>
      <ReferenceInput
        source="guest"
        reference="guests"
      >
        <AutocompleteInput optionText="name" />
      </ReferenceInput>
      <ReferenceInput source="apartment" reference="apartments"><SelectInput optionText="name" /></ReferenceInput>
      <DateInput source="check_in" />
      <DateInput source="check_out" />
      <NumberInput source="num_guests" />
      <ReferenceInput source="booking_source" reference="booking-sources">
        <SelectInput optionText="name" label="Źródło rezerwacji" />
      </ReferenceInput>
      <NumberInput source="price" label="Cena brutto" />
      <FormDataConsumer>
        {({ formData }) => <CommissionCalculator formData={formData} />}
      </FormDataConsumer>
      <NumberInput source="commission" label="Prowizja (auto)" helperText="Automatycznie wyliczana na podstawie źródła" />
      <NumberInput source="discount" label="Rabat" />
      <NumberInput source="total" label="Kwota netto (auto)" helperText="Automatycznie wyliczana: cena - prowizja" />
      <TextInput source="status" />
      <TextInput source="payment_status" />
    </SimpleForm>
  </Create>
);
